/**
 * MEVIA - Culture Tab (Dimensions)
 * Shows company profile evaluation, dimensions assessments, and HR indicators
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Users, 
  CheckCircle, 
  Clock, 
  BarChart3,
  PlusCircle,
  Eye,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import HRIndicatorsPanel from './HRIndicatorsPanel';
import HRIndividualReport from './HRIndividualReport';
import IPSDepartmentDashboard from './IPSDepartmentDashboard';
import logger from '../../utils/logger';

const CultureTab = ({ companyInfo }) => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ completed: 0, pending: 0, inProgress: 0 });
  const [companyAssessments, setCompanyAssessments] = useState([]);
  
  // HR Individual Report state
  const [showHRReport, setShowHRReport] = useState(false);
  const [selectedEmployeeForHR, setSelectedEmployeeForHR] = useState(null);
  const [employeesWithIndicators, setEmployeesWithIndicators] = useState([]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [empResponse, assessResponse] = await Promise.all([
        axios.get('/companies/employees-and-candidates'),
        axios.get('/assessments')
      ]);
      
      if (empResponse.data.success) {
        const allUsers = empResponse.data.data || [];
        const emps = allUsers.filter(u => u.rol === 'employee');
        const cands = allUsers.filter(u => u.rol === 'candidate');
        setEmployees(emps);
        setCandidates(cands);
        
        // Calculate stats based on completed_assessments (culture)
        const completed = allUsers.filter(u => (u.completed_assessments || 0) > 0).length;
        const pending = allUsers.filter(u => !u.completed_assessments || u.completed_assessments === 0).length;
        setStats({ completed, pending, inProgress: 0 });
      }
      
      if (assessResponse.data.success) {
        // Filter company assessments
        const companyAssess = (assessResponse.data.data || []).filter(
          a => a.tipo === 'company_complete' || a.tipo === 'company_express'
        );
        setCompanyAssessments(companyAssess);
      }
    } catch (error) {
      logger.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load employees with HR indicators
  const loadEmployeesWithIndicators = async () => {
    try {
      const res = await axios.get(`/v2/hr-indicators/company/${user?.empresa_id}?source=dimensions`);
      if (res.data.success) {
        setEmployeesWithIndicators(res.data.data.employees || []);
      }
    } catch (error) {
      logger.error('Error loading employees with indicators:', error);
    }
  };

  // Handle viewing individual HR report
  const handleViewHRReport = (employee) => {
    setSelectedEmployeeForHR(employee);
    setShowHRReport(true);
  };

  // Handle viewing report from an alert
  const handleViewAlertReport = async (alert) => {
    try {
      // First, load employees with indicators if not loaded
      let empList = employeesWithIndicators;
      if (empList.length === 0) {
        const res = await axios.get(`/v2/hr-indicators/company/${user?.empresa_id}?source=dimensions`);
        if (res.data.success) {
          empList = res.data.data.employees || [];
          setEmployeesWithIndicators(empList);
        }
      }
      
      // Find the employee by usuario_id
      const employee = empList.find(emp => emp.user_id === alert.usuario_id);
      
      if (employee) {
        handleViewHRReport(employee);
      } else {
        // If employee not found in the list, create a basic object with available info
        handleViewHRReport({
          user_id: alert.usuario_id,
          nombre: alert.usuario_nombre,
          email: alert.usuario_email,
          assessment_id: null
        });
      }
    } catch (error) {
      logger.error('Error loading employee for alert:', error);
      toast.error(language === 'es' ? 'Error al cargar reporte' : 'Error loading report');
    }
  };

  const createCompanyAssessment = async () => {
    try {
      const response = await axios.post('/assessments', {
        tipo: 'company_complete',
        idioma: language
      });
      
      if (response.data.success) {
        toast.success(language === 'es' ? 'Evaluación creada' : 'Assessment created');
        navigate(`/assessment/${response.data.data.id}`);
      }
    } catch (error) {
      logger.error('Error creating assessment:', error);
      toast.error(language === 'es' ? 'Error al crear evaluación' : 'Error creating assessment');
    }
  };

  // If showing HR individual report
  if (showHRReport && selectedEmployeeForHR) {
    return (
      <HRIndividualReport
        employee={selectedEmployeeForHR}
        assessmentId={selectedEmployeeForHR.assessment_id}
        source="dimensions"
        onBack={() => {
          setShowHRReport(false);
          setSelectedEmployeeForHR(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Buttons Row - Only Reports buttons */}
      <div className="flex flex-wrap gap-2">
        <Button 
          onClick={() => navigate('/reports')}
          style={{ backgroundColor: '#1A878D' }}
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          {language === 'es' ? 'Ver Reportes' : 'View Reports'}
        </Button>
        
        <Button 
          variant="outline" 
          onClick={async () => {
            await loadEmployeesWithIndicators();
            if (employeesWithIndicators.length === 0) {
              await loadEmployeesWithIndicators();
            }
          }}
          className="border-purple-500 text-purple-700 hover:bg-purple-50"
        >
          <FileText className="h-4 w-4 mr-2" />
          {language === 'es' ? 'Reportes HR Individuales' : 'Individual HR Reports'}
        </Button>
      </div>

      {/* Employee HR Reports Dropdown with Search */}
      {employeesWithIndicators.length > 0 && (
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-600" />
                <span className="font-medium text-purple-700">
                  {language === 'es' ? 'Reporte HR Individual:' : 'Individual HR Report:'}
                </span>
              </div>
              
              {/* Searchable Dropdown */}
              <div className="relative flex-1 min-w-[250px] max-w-md">
                <input
                  type="text"
                  placeholder={language === 'es' ? 'Buscar empleado...' : 'Search employee...'}
                  value={employeeSearchTerm}
                  onChange={(e) => {
                    setEmployeeSearchTerm(e.target.value);
                    setShowEmployeeDropdown(true);
                  }}
                  onFocus={() => setShowEmployeeDropdown(true)}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                
                {/* Dropdown List */}
                {showEmployeeDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-purple-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {employeesWithIndicators
                      .filter(emp => 
                        emp.nombre?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                        emp.email?.toLowerCase().includes(employeeSearchTerm.toLowerCase())
                      )
                      .map((emp, index) => (
                        <div 
                          key={`${emp.user_id}-${index}`}
                          onClick={() => {
                            handleViewHRReport(emp);
                            setShowEmployeeDropdown(false);
                            setEmployeeSearchTerm('');
                          }}
                          className="flex items-center justify-between px-3 py-2 hover:bg-purple-100 cursor-pointer border-b border-purple-50 last:border-0"
                        >
                          <div>
                            <p className="font-medium text-sm">{emp.nombre}</p>
                            <p className="text-xs text-gray-500">{emp.email}</p>
                          </div>
                          <Eye className="h-4 w-4 text-purple-600" />
                        </div>
                      ))
                    }
                    {employeesWithIndicators.filter(emp => 
                      emp.nombre?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                      emp.email?.toLowerCase().includes(employeeSearchTerm.toLowerCase())
                    ).length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500 text-center">
                        {language === 'es' ? 'No se encontraron empleados' : 'No employees found'}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                {employeesWithIndicators.length} {language === 'es' ? 'disponibles' : 'available'}
              </Badge>
              
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setEmployeesWithIndicators([]);
                  setShowEmployeeDropdown(false);
                  setEmployeeSearchTerm('');
                }}
                className="text-purple-600 hover:text-purple-800"
              >
                ✕
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Click outside to close dropdown */}
      {showEmployeeDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowEmployeeDropdown(false)}
        />
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">
                  {language === 'es' ? 'Completadas' : 'Completed'}
                </p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">
                  {language === 'es' ? 'Pendientes' : 'Pending'}
                </p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <Clock className="h-6 w-6 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">
                  {language === 'es' ? 'Empleados' : 'Employees'}
                </p>
                <p className="text-2xl font-bold text-blue-600">{employees.length}</p>
              </div>
              <Users className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">
                  {language === 'es' ? 'Candidatos' : 'Candidates'}
                </p>
                <p className="text-2xl font-bold text-purple-600">{candidates.length}</p>
              </div>
              <Users className="h-6 w-6 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* HR Indicators Section - Culture Context (Company Level) */}
      <HRIndicatorsPanel
        context="culture"
        indicatorsToShow={['irr', 'ico', 'iap', 'ipc', 'icf', 'ipe', 'ilo']}
        showAlerts={true}
        showEmployees={false}
        showCandidates={false}
        title={language === 'es' ? 'Indicadores de Cultura' : 'Culture Indicators'}
        description={language === 'es' 
          ? 'Métricas generales de la empresa basadas en las 10 Dimensiones'
          : 'Company-level metrics based on the 10 Dimensions'}
        onViewAlertReport={handleViewAlertReport}
        onViewEmployeeReport={(emp) => handleViewHRReport(emp)}
      />

      {/* IPS by Department Dashboard */}
      <IPSDepartmentDashboard 
        onViewEmployeeReport={(emp) => handleViewHRReport(emp)}
      />

      {/* Company Assessment Section */}
      <Card className="border-2" style={{ borderColor: '#1A878D' }}>
        <CardHeader>
          <CardTitle className="flex items-center text-lg">
            <span className="text-xl mr-2">🏢</span>
            {language === 'es' ? 'Evaluación de Empresa' : 'Company Assessment'}
          </CardTitle>
          <CardDescription>
            {language === 'es' 
              ? 'Evaluación por tokens de lo que ofrece y necesita tu organización en las 10 dimensiones' 
              : 'Token-based assessment of what your organization offers and needs in the 10 dimensions'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {companyAssessments.length > 0 ? (
            <div className="space-y-4">
              {/* Show existing company assessments */}
              {companyAssessments.map((assessment) => (
                <div 
                  key={assessment.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">
                      {language === 'es' ? 'Evaluación Empresarial' : 'Company Assessment'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {assessment.status === 'completado' 
                        ? (language === 'es' ? 'Completada' : 'Completed')
                        : (language === 'es' ? 'En progreso' : 'In progress')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {assessment.status === 'completado' ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {language === 'es' ? 'Completada' : 'Completed'}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <Clock className="h-3 w-3 mr-1" />
                        {language === 'es' ? 'En progreso' : 'In progress'}
                      </Badge>
                    )}
                    <Button 
                      size="sm" 
                      onClick={() => navigate(`/assessment/${assessment.id}`)}
                      style={{ backgroundColor: '#1A878D' }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      {assessment.status === 'completado' 
                        ? (language === 'es' ? 'Ver Resultados' : 'View Results')
                        : (language === 'es' ? 'Continuar' : 'Continue')}
                    </Button>
                  </div>
                </div>
              ))}
              
              {/* Message when company assessment already exists */}
              {companyAssessments.some(a => a.status === 'completado') ? (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <p className="text-sm text-blue-700">
                    {language === 'es' 
                      ? 'Ya se ha aplicado la evaluación de empresa. Puedes ver los resultados arriba.' 
                      : 'Company assessment has already been applied. You can view the results above.'}
                  </p>
                </div>
              ) : (
                /* Button only shows if no completed assessment */
                <Button 
                  variant="outline" 
                  onClick={createCompanyAssessment}
                  className="w-full"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Nueva Evaluación de Empresa' : 'New Company Assessment'}
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500 mb-4">
                {language === 'es' 
                  ? 'Aún no has creado una evaluación de empresa' 
                  : 'You haven\'t created a company assessment yet'}
              </p>
              <Button onClick={createCompanyAssessment} style={{ backgroundColor: '#1A878D' }}>
                <PlusCircle className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Iniciar Evaluación de Empresa' : 'Start Company Assessment'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employees List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <Users className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
              {language === 'es' ? 'Empleados' : 'Employees'}
            </span>
            <Badge variant="secondary">{employees.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-6 text-gray-500">
              {language === 'es' ? 'Cargando...' : 'Loading...'}
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              {language === 'es' ? 'No hay empleados registrados' : 'No employees registered'}
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {employees.map((emp) => (
                <div key={emp.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{emp.nombre}</p>
                    <p className="text-xs text-gray-500">{emp.email}</p>
                    {emp.departamento && (
                      <Badge variant="outline" className="mt-1 text-xs">{emp.departamento}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {emp.completed_assessments || 0}/{emp.total_assessments || 1}
                    </span>
                    {(emp.completed_assessments || 0) > 0 ? (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {language === 'es' ? 'Completado' : 'Completed'}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {language === 'es' ? 'Pendiente' : 'Pending'}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Candidates List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <Users className="h-5 w-5 mr-2 text-purple-600" />
              {language === 'es' ? 'Candidatos' : 'Candidates'}
            </span>
            <Badge variant="secondary">{candidates.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {candidates.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              {language === 'es' ? 'No hay candidatos registrados' : 'No candidates registered'}
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {candidates.map((cand) => (
                <div key={cand.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{cand.nombre}</p>
                    <p className="text-xs text-gray-500">{cand.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {cand.completed_assessments || 0}/{cand.total_assessments || 1}
                    </span>
                    {(cand.completed_assessments || 0) > 0 ? (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {language === 'es' ? 'Completado' : 'Completed'}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {language === 'es' ? 'Pendiente' : 'Pending'}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CultureTab;
